import React, { useState, useEffect } from 'react';
import { MapPin, Plus, Trash2, Edit2, Save, Building, Loader2, ExternalLink, Ticket, Link2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Switch } from '../ui/switch';
import { cinemasApi } from '../../services/api';

const AdminCinemas = () => {
  const [cinemas, setCinemas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingCinema, setEditingCinema] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    googleMapsUrl: '',
    logo: '',
    website: '',
    coordinates: { lat: 0, lng: 0 },
    ticketOnlyMode: false,
    ticketUrl: ''
  });

  useEffect(() => {
    fetchCinemas();
  }, []);

  const fetchCinemas = async () => {
    try {
      const res = await cinemasApi.getAll();
      setCinemas(res.data);
    } catch (error) {
      console.error('Error fetching cinemas:', error);
    } finally {
      setLoading(false);
    }
  };

  const openNewCinemaDialog = () => {
    setFormData({ 
      name: '', 
      address: '', 
      googleMapsUrl: '',
      logo: '', 
      website: '',
      coordinates: { lat: 0, lng: 0 },
      ticketOnlyMode: false,
      ticketUrl: ''
    });
    setEditingCinema(null);
    setIsDialogOpen(true);
  };

  const openEditDialog = (cinema) => {
    setFormData({
      name: cinema.name,
      address: cinema.address,
      googleMapsUrl: cinema.googleMapsUrl || '',
      logo: cinema.logo || '',
      website: cinema.website || '',
      coordinates: cinema.coordinates || { lat: 0, lng: 0 },
      ticketOnlyMode: cinema.ticketOnlyMode || false,
      ticketUrl: cinema.ticketUrl || ''
    });
    setEditingCinema(cinema);
    setIsDialogOpen(true);
  };

  // Extract coordinates from Google Maps URL
  const extractCoordsFromUrl = (url) => {
    if (!url) return null;
    
    // Try to extract from @lat,lng format
    const atMatch = url.match(/@(-?\d+\.?\d*),(-?\d+\.?\d*)/);
    if (atMatch) {
      return { lat: parseFloat(atMatch[1]), lng: parseFloat(atMatch[2]) };
    }
    
    // Try to extract from q=lat,lng format
    const qMatch = url.match(/[?&]q=(-?\d+\.?\d*),(-?\d+\.?\d*)/);
    if (qMatch) {
      return { lat: parseFloat(qMatch[1]), lng: parseFloat(qMatch[2]) };
    }
    
    return null;
  };

  const handleGoogleMapsUrlChange = (url) => {
    setFormData({ ...formData, googleMapsUrl: url });
    
    // Try to auto-extract coordinates
    const coords = extractCoordsFromUrl(url);
    if (coords) {
      setFormData(prev => ({ ...prev, googleMapsUrl: url, coordinates: coords }));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const dataToSave = {
        name: formData.name,
        address: formData.address,
        googleMapsUrl: formData.googleMapsUrl,
        logo: formData.logo,
        website: formData.website,
        coordinates: formData.coordinates,
        ticketOnlyMode: formData.ticketOnlyMode,
        ticketUrl: formData.ticketUrl
      };
      
      if (editingCinema) {
        await cinemasApi.update(editingCinema.id, dataToSave);
      } else {
        await cinemasApi.create(dataToSave);
      }
      await fetchCinemas();
      setIsDialogOpen(false);
    } catch (error) {
      console.error('Error saving cinema:', error);
      alert('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const deleteCinema = async (id) => {
    if (window.confirm('¿Estás seguro de eliminar este cine?')) {
      try {
        await cinemasApi.delete(id);
        await fetchCinemas();
      } catch (error) {
        console.error('Error deleting cinema:', error);
        alert('Error al eliminar');
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Cines</h1>
          <p className="text-gray-500">Gestiona los cines donde se proyecta la película</p>
        </div>
        <Button onClick={openNewCinemaDialog} className="bg-[#50c878] hover:bg-[#40a0a0]" data-testid="add-cinema-btn">
          <Plus className="w-4 h-4 mr-2" />
          Añadir Cine
        </Button>
      </div>

      {/* Cinemas Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {cinemas.map((cinema) => (
          <Card key={cinema.id} className="overflow-hidden" data-testid={`cinema-card-${cinema.id}`}>
            <CardHeader className="pb-2">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden">
                    {cinema.logo ? (
                      <img 
                        src={cinema.logo} 
                        alt={cinema.name}
                        className="w-10 h-10 object-contain"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <Building className="w-6 h-6 text-gray-400" />
                    )}
                  </div>
                  <div>
                    <CardTitle className="text-lg">{cinema.name}</CardTitle>
                    {cinema.ticketOnlyMode && (
                      <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded mt-1">
                        <Ticket className="w-3 h-3" />
                        Solo enlace
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 text-sm text-gray-600 mb-4">
                <div className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>{cinema.address}</span>
                </div>
                {cinema.googleMapsUrl && (
                  <a 
                    href={cinema.googleMapsUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-[#50c878] hover:underline"
                  >
                    <ExternalLink className="w-3 h-3" />
                    Ver en Google Maps
                  </a>
                )}
                {cinema.ticketUrl && (
                  <div className="flex items-center gap-1 text-xs text-gray-500">
                    <Link2 className="w-3 h-3" />
                    <span className="truncate">{cinema.ticketUrl}</span>
                  </div>
                )}
                {cinema.coordinates && (
                  <p className="text-xs text-gray-400">
                    Coords: {cinema.coordinates.lat?.toFixed(4)}, {cinema.coordinates.lng?.toFixed(4)}
                  </p>
                )}
              </div>
              <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                <span className="text-sm text-gray-500">
                  {cinema.ticketOnlyMode ? 'Enlace directo' : `${cinema.showtimes?.length || 0} sesiones`}
                </span>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => openEditDialog(cinema)} data-testid={`edit-cinema-${cinema.id}`}>
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => deleteCinema(cinema.id)}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    data-testid={`delete-cinema-${cinema.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingCinema ? 'Editar Cine' : 'Nuevo Cine'}
            </DialogTitle>
            <DialogDescription>
              Configura los detalles del cine
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="name">Nombre del Cine *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ej: Cines Yelmo Plaza Mayor"
                className="mt-1"
                data-testid="cinema-name-input"
              />
            </div>
            
            <div>
              <Label htmlFor="address">Dirección *</Label>
              <Input
                id="address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="Ej: Centro Comercial Plaza Mayor, Málaga"
                className="mt-1"
                data-testid="cinema-address-input"
              />
              <p className="text-xs text-gray-500 mt-1">Dirección exacta como aparece en Google Maps</p>
            </div>
            
            <div>
              <Label htmlFor="googleMapsUrl">URL de Google Maps</Label>
              <Input
                id="googleMapsUrl"
                value={formData.googleMapsUrl}
                onChange={(e) => handleGoogleMapsUrlChange(e.target.value)}
                placeholder="https://maps.google.com/..."
                className="mt-1"
                data-testid="cinema-googlemaps-input"
              />
              <p className="text-xs text-gray-500 mt-1">Copia el enlace de Google Maps. Las coordenadas se extraerán automáticamente.</p>
            </div>
            
            <div>
              <Label htmlFor="logo">URL del Logo</Label>
              <Input
                id="logo"
                value={formData.logo}
                onChange={(e) => setFormData({ ...formData, logo: e.target.value })}
                placeholder="https://ejemplo.com/logo.png"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="website">Web del cine</Label>
              <Input
                id="website"
                value={formData.website}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                placeholder="https://www.cine.com"
                className="mt-1"
              />
            </div>
            
            {/* Ticket Only Mode Section */}
            <div className="p-4 border rounded-lg bg-gray-50 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="ticketOnlyMode" className="text-base font-medium">Modo "Solo comprar entradas"</Label>
                  <p className="text-xs text-gray-500">Mostrar solo un botón de compra sin horarios específicos</p>
                </div>
                <Switch
                  id="ticketOnlyMode"
                  checked={formData.ticketOnlyMode}
                  onCheckedChange={(checked) => setFormData({ ...formData, ticketOnlyMode: checked })}
                  data-testid="ticket-only-mode-switch"
                />
              </div>
              
              <div>
                <Label htmlFor="ticketUrl">URL de compra de entradas {formData.ticketOnlyMode && '*'}</Label>
                <Input
                  id="ticketUrl"
                  value={formData.ticketUrl}
                  onChange={(e) => setFormData({ ...formData, ticketUrl: e.target.value })}
                  placeholder="https://cine.com/comprar/pelicula"
                  className="mt-1"
                  data-testid="ticket-url-input"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {formData.ticketOnlyMode 
                    ? 'Este enlace se mostrará como botón "Comprar entradas"' 
                    : 'Este enlace se usará como valor por defecto para nuevas sesiones'}
                </p>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="lat">Latitud</Label>
                <Input
                  id="lat"
                  type="number"
                  step="any"
                  value={formData.coordinates?.lat || ''}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    coordinates: { ...formData.coordinates, lat: parseFloat(e.target.value) || 0 }
                  })}
                  placeholder="40.4168"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="lng">Longitud</Label>
                <Input
                  id="lng"
                  type="number"
                  step="any"
                  value={formData.coordinates?.lng || ''}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    coordinates: { ...formData.coordinates, lng: parseFloat(e.target.value) || 0 }
                  })}
                  placeholder="-3.7038"
                  className="mt-1"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={saving || (formData.ticketOnlyMode && !formData.ticketUrl)} 
              className="bg-[#50c878] hover:bg-[#40a0a0]"
              data-testid="save-cinema-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCinemas;
